import {
  apiErrorHasCode,
  ChatMessageAttachmentType,
  EmailMessageModel,
  SendWhatsappMessageMutationParams,
  useRestApiProvider,
  whatsappConversations$,
  WhatsappMessageModel,
  WhatsappTemplateModel,
} from "@jugl-web/rest-api";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import {
  FC,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useTranslations } from "@jugl-web/utils";
import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import { AttachmentType } from "@web-src/modules/chats/types";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { WhatsappTemplatesDialog } from "../WhatsappTemplatesDialog";
import { useGetSenderDetails } from "../../hooks/useGetSenderDetails";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { isWebMessage } from "../../../../utils/typeGuards";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { DisconnectedChatInput } from "../DisconnectedChatInput";
import { CRMInvalidUserPopup } from "../CRMInvalidUserPopup";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";

export const WhatsappChatInput: FC<{
  chatId: string;
  onMessageSent?: (message: WhatsappMessageModel) => void;
}> = ({ chatId, onMessageSent }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { reconnectWhatsappAccount, isRegisteringAccount, isConnected } =
    useWhatsappAccount();
  const {
    selectedConversation,
    isChatExpired,
    replyMessage: contextReplyMessage,
    setReplyMessage,
    incomingWhatsappMessages$,
    setIsSendingMessage,
  } = useClientSupportPageWithSelectedConversation();
  // the whatsapp input never deals with email replies
  const replyMessage = contextReplyMessage as Exclude<
    typeof contextReplyMessage,
    EmailMessageModel
  >;
  const { getSenderDetails } = useGetSenderDetails();
  const { senderName } = getSenderDetails(replyMessage);
  const meId = useSelector(selectUserId);
  const { profile: meProfile } = useMe();
  const [isInvalidUserPopupOpen, setIsInvalidUserPopupOpen] = useState(false);

  const { safeProfile: userSafeProfile } = useUserGeneralProfile({
    entityId,
    userId: replyMessage?.from,
    skip: !replyMessage || replyMessage.type !== "outgoing" || !!senderName,
  });

  const replyTitle = useMemo(() => {
    if (!replyMessage) return undefined;

    const name = senderName || userSafeProfile.displayName;
    const isYou = replyMessage.from === meId;

    return `${name}${
      isYou
        ? ` (${t({
            id: "common.you",
            defaultMessage: "You",
          })})`
        : ""
    }`;
  }, [senderName, userSafeProfile, replyMessage, meId, t]);

  const [sendMessage, { isLoading: isSendingMessage }] =
    integrationsApi.useSendWhatsappMessageMutation();
  const [uploadMedia, { isLoading: isUploadingMedia }] =
    integrationsApi.useUploadWhatsappMediaMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);
  const [isTemplatesDialogOpen, setIsTemplatesDialogOpen] = useState(false);

  useEffect(() => {
    if (replyMessage) {
      chatInput$.current?.focus();
    }
  }, [replyMessage]);

  const handleSendMessage: ChatInputParams["onSubmit"] = async ({
    body,
    attachments,
  }) => {
    setIsSendingMessage(true);
    try {
      const data: SendWhatsappMessageMutationParams = {
        entityId,
        data: {
          conv_name: selectedConversation.name,
          to: selectedConversation.conv_id,
          type: "text",
          payload: {
            body,
          },
          context: replyMessage
            ? {
                id: replyMessage.msg_id,
                from: replyMessage.from,
              }
            : undefined,
        },
        meta: {
          onError: (error) => {
            if (apiErrorHasCode(error, "invalid_user_id")) {
              setIsInvalidUserPopupOpen(true);
              return undefined;
            }
            return "unhandled";
          },
        },
      };
      const attachment = attachments?.[0];
      if (attachment) {
        const { id: mediaId } = await uploadMedia({
          entityId,
          file: attachment.file,
          meta: { silent: true },
        }).unwrap();
        switch (attachment.type) {
          case ChatMessageAttachmentType.image:
            data.data = {
              ...data.data,
              type: "image",
              payload: {
                id: mediaId,
                caption: body,
              },
            };
            break;
          case ChatMessageAttachmentType.audio:
          case ChatMessageAttachmentType.voice:
            data.data = {
              ...data.data,
              type: "audio",
              payload: {
                id: mediaId,
              },
            };
            break;
          case ChatMessageAttachmentType.video:
            data.data = {
              ...data.data,
              type: "video",
              payload: {
                id: mediaId,
                caption: body,
              },
            };
            break;
          default:
            data.data = {
              ...data.data,
              type: "document",
              payload: {
                id: mediaId,
                caption: body,
                filename: attachment.file.name,
              },
            };
            break;
        }
      }
      const message = await sendMessage(data).unwrap();
      const messageWithReply = {
        ...message,
        reply: replyMessage as WhatsappMessageModel,
        attended_by: meProfile?.displayName,
      };
      const updatedConversation = reactivateClosedConversation({
        ...(selectedConversation as WhatsappConversationModel),
        ...(messageWithReply as WhatsappMessageModel),
        attended_by: meProfile?.firstName || "",
      });
      whatsappConversations$.next({
        type: "update",
        conversation: updatedConversation,
      });
      onMessageSent?.(messageWithReply);
      chatInput$.current?.clear();
      chatInput$.current?.focus();
      setReplyMessage(selectedConversation.conv_id, undefined);
    } catch {
      // Error UI handled by apiResultMiddleware via meta / default toast
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleSendTemplate = async (template: WhatsappTemplateModel) => {
    const message = await sendMessage({
      entityId,
      data: {
        to: selectedConversation.conv_id,
        conv_name: selectedConversation.name,
        name: template.name,
      },
    }).unwrap();
    incomingWhatsappMessages$.next(message);
    onMessageSent?.(message);
  };

  const replyMessageText = useMemo(() => {
    if (
      !replyMessage ||
      !("type" in replyMessage.payload) ||
      isWebMessage(replyMessage)
    )
      return "";

    switch (replyMessage.payload.type) {
      case "text":
        return (
          (replyMessage.type !== "info" && replyMessage.payload.text?.body) ||
          ""
        );
      case "image":
        return (
          replyMessage.payload.image?.caption ||
          t({
            id: "client-support-page.reply-image",
            defaultMessage: "Image",
          })
        );
      case "video":
        return (
          replyMessage.payload.video?.caption ||
          t({
            id: "client-support-page.reply-video",
            defaultMessage: "Video",
          })
        );
      case "audio":
        return t({
          id: "client-support-page.reply-audio",
          defaultMessage: "Audio message",
        });
      case "document":
        return (
          replyMessage.payload.document?.filename ||
          t({
            id: "client-support-page.reply-document",
            defaultMessage: "Document",
          })
        );
      default:
        return t({
          id: "client-support-page.unsupported-message-format",
          defaultMessage: "Unsupported message format",
        });
    }
  }, [replyMessage, t]);

  const handleCancelReply = useCallback(() => {
    setReplyMessage(selectedConversation.conv_id, undefined);
  }, [selectedConversation.conv_id, setReplyMessage]);

  if (!isConnected) {
    return (
      <DisconnectedChatInput
        title={t({
          id: "client-support-page.account-is-disconnected",
          defaultMessage: "Account is Disconnected",
        })}
        description={t(
          {
            id: "client-support-page.please-reconnect-to-continue-using-whatsapp-inbox",
            defaultMessage:
              "Please <highlightedText>reconnect</highlightedText> to continue using WhatsApp Inbox",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="font-medium text-primary">{text}</span>
            ),
          }
        )}
        buttonProps={{
          text: t({
            id: "common.reconnect",
            defaultMessage: "Reconnect",
          }),
          onClick: reconnectWhatsappAccount,
          isDisabled: isRegisteringAccount,
        }}
      />
    );
  }

  if (isChatExpired) {
    return (
      <>
        <WhatsappTemplatesDialog
          isOpen={isTemplatesDialogOpen}
          onRequestClose={() => setIsTemplatesDialogOpen(false)}
          isSending={isSendingMessage}
          onSelectTemplate={handleSendTemplate}
        />
        <DisconnectedChatInput
          variant="expired"
          title={t({
            id: "client-support-page.chat-is-expired",
            defaultMessage: "Chat is Expired",
          })}
          description={t(
            {
              id: "client-support-page.whatsapp-chat-is-expired-use-templates",
              defaultMessage:
                "Conversation is automatically marked as expired after 24 hours of contact inactivity. You can send messages only by using <highlightedText>WhatsApp Templates</highlightedText> ℹ️",
            },
            {
              highlightedText: (text: ReactNode) => (
                <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                  {text}
                </span>
              ),
            }
          )}
          buttonProps={{
            text: t({
              id: "client-support-page.select-template",
              defaultMessage: "Select Template",
            }),
            onClick: () => setIsTemplatesDialogOpen(true),
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="border-0 border-t border-solid border-dark-100 px-8 py-6">
        <ChatInput
          variant="inbox"
          id={chatId}
          ref={chatInput$}
          onSubmit={handleSendMessage}
          isLoading={isSendingMessage || isUploadingMedia}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: true,
              accept: {
                [AttachmentType.audio]: ".aac,.amr,.mp3,.m4a,.ogg",
                [AttachmentType.file]:
                  ".txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf",
                [AttachmentType.image]: ".jpeg,.png",
                [AttachmentType.video]: ".3gp,.mp4",
              },
              maxSize: {
                [AttachmentType.audio]: 16,
                [AttachmentType.file]: 100,
                [AttachmentType.image]: 5,
                [AttachmentType.video]: 16,
              },
              isCaptionHidden: true,
            },
          }}
          actionMessageInfo={
            replyMessage
              ? {
                  type: "reply",
                  text: replyMessageText,
                  title: replyTitle,
                  onClose: handleCancelReply,
                }
              : undefined
          }
        />
      </div>
      <CRMInvalidUserPopup
        isOpen={isInvalidUserPopupOpen}
        onRequestClose={() => setIsInvalidUserPopupOpen(false)}
      />
    </>
  );
};
